<template>
    <div>
      <h4 class="title">Carrier Activity</h4>
      <b-select v-model="selectedCarrierIndex" :loading="isLoading" :disabled="isLoading" expanded>
        <option v-for="(carrier, index) in carriers" :value="index" :key="carrier.value">
          {{ carrier.display }}
        </option>
      </b-select>
      <b-select v-model="selectedPeriod" :loading="isLoading" :disabled="isLoading" class="period-picker" expanded>
        <option v-for="date in dateList" :value="date.value" :key="date.value">
          {{ date.display }}
        </option>
      </b-select>

      <ul>
        <li>
          {{ formatNumber(metrics.companyJobCount) }}
          Completed Transactions
        </li>
        <li>
          {{ formatNumber(metrics.carrierCount) }}
          Carriers Used
        </li>
        <li>
          {{ formatNumber(metrics.companyJobDocumentCount) }}
          Documents Used
        </li>
        <li>
          {{ formatNumber(metrics.companyJobMilesDrivenSum) }}
          Miles Driven
        </li>
        <li>
          {{ formatCurrency(metrics.companyJobRateSum) }}
          Cumulative Job Rate
        </li>
        <li>
          {{ formatPercent(metrics.milestoneCompletionAverage) }}
          Milestones Completed
        </li>
      </ul>

      <b-notification type="is-danger" :closable="false" :active="hasError">
        There was an error fetching the metrics for the selected period.
      </b-notification>
    </div>
</template>

<script>
import MetricsApi from '@/api-interface/metrics'
import RolodexApi from '@/api-interface/rolordex'
import { format, differenceInCalendarMonths, addMonths } from 'date-fns'
import { mapGetters } from 'vuex'
import metricsMixin from './metricsMixIn'

export default {
  name: 'ShipperActivityByCarrier',
  mixins: [metricsMixin],

  async mounted() {
    await this.fetchCarrierList()
    this.fetchMetrics()
  },

  data() {
    return {
      metrics: {},
      carriers: [],
      selectedPeriod: format(new Date(), 'YYYYMM'),
      selectedCarrierIndex: 0,
      isLoading: false,
      hasError: false
    }
  },

  computed: {
    ...mapGetters('user', ['accountInfo']),

    dateList() {
      const earliest = new Date(this.accountInfo.company.createdTimestamp || this.accountInfo.company._CreatedTimestamp)
      const currentDate = new Date()
      const diff = differenceInCalendarMonths(currentDate, earliest)

      const list = []
      for (let i = diff; i >= 0; i--) {
        const target = addMonths(earliest, i)
        const value = format(target, 'YYYYMM')
        const formatted = format(target, 'YYYY MMMM')
        const display = i === diff ? `${formatted} - Current` : formatted
        list.push({ value, display })
      }

      return list
    }
  },

  methods: {
    async fetchCarrierList() {
      this.carriers = (await RolodexApi.carrierSearch({ searchString: '%' })).map((carrier) => ({
        value: carrier.accountKey,
        display: `${carrier.accountFirstName} ${carrier.accountLastName}`
      }))
    },

    async fetchMetrics() {
      try {
        this.isLoading = true
        this.hasError = false
        const companyKey = this.accountInfo.company.companyKey
        const accountKey = (this.carriers[this.selectedCarrierIndex] || {}).value
        const rawMetrics = await MetricsApi.list({ companyKey, accountKey, period: this.selectedPeriod })
        // const rawMetrics = await MetricsApi.list({ companyKey: this.companyKey, period: '201903' })
        this.metrics = rawMetrics.find((metric) => metric.carrierLevel)
      } catch (err) {
        this.hasError = true
        console.error('Unable to fetch metrics. ', err)
        this.resetMetrics()
      } finally {
        this.isLoading = false
      }
    }
  },

  watch: {
    selectedPeriod: 'fetchMetrics',
    selectedCarrierIndex: 'fetchMetrics'
  }
}
</script>

<style scoped lang="scss">
.title {
  font-size: 1.2rem;
}
.period-picker {
  margin-bottom: 16px;
  width: 100%;
}

li {
  padding: 8px 0;
}
</style>
